import Channel from '@/Enums/Channel.enum';
import { IAccordionInputProps, ILocation } from '@/Interfaces/Forecaster';
import LocationChips from '@/components/Geography/LocationChips';
import { GEO_TARGETING_LOCATIONS_LOOKUP } from '@/constants/endpoints';
import { MAP_SCALES } from '@/constants/geo';
import { STATES_REGEX, ZIP_CODES_REGEX } from '@/constants/geography';
import {
  getGeoSearchPlaceholder,
  handlePasteStatesAndZipCodes,
  handlePasteZipCodes,
} from '@/helpers/forecaster/geography';
import { useAppDispatch } from '@/store/hooks';
import { getGeoOptions } from '@/store/slices/forecasterSlice';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { Box, Button, Collapse } from '@mui/material';
import { ArrowDropDownIcon } from '@mui/x-date-pickers';
import axios from 'axios';
import debounce from 'debounce';
import React, { ClipboardEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';
import GeoTargeting from './GeoTargeting';

const GeoExclusion: React.FC<IAccordionInputProps> = ({ loading, options, currentChannel }) => {
  const { control, setValue } = useFormContext();
  const dispatch = useAppDispatch();

  //#region state
  const [query, setQuery] = useState('');
  const [loadingLookup, setLoadingLookup] = useState(false);
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<ILocation[]>([]);

  //#endregion

  const excludedLocations = useWatch({ name: 'geoExclusion', control });
  const onSelectedLocationsChange = useCallback(
    (newSelectedLocations: ILocation[]) => {
      setValue('geoExclusion', newSelectedLocations?.length ? newSelectedLocations : []);
    },
    [setValue],
  );

  const geoFence = useWatch({ name: 'geoFence', control });

  //#region callback
  const handleInput = useCallback((event: { type: string }, value: React.SetStateAction<string>) => {
    if (event?.type !== 'change') return;
    setQuery(value);
  }, []);

  const onValuesPaste = useCallback(
    async (event: ClipboardEvent<HTMLInputElement>) => {
      const value = (event.clipboardData || window.Clipboard).getData('text').trim();

      let zips,
        states = [];

      if (ZIP_CODES_REGEX.test(value)) {
        zips = handlePasteZipCodes(value, excludedLocations);
      }

      if (STATES_REGEX.test(value)) {
        const res = handlePasteStatesAndZipCodes(value, excludedLocations);
        states = res.states;
        zips = res.zipCodes;
      }

      if ((!zips && !states) || (!zips?.length && !states?.length)) {
        return;
      }

      try {
        event.preventDefault();

        setLoadingLookup(true);
        const payload = await axios.post(GEO_TARGETING_LOCATIONS_LOOKUP, {
          zips,
          states,
        });
        setLoadingLookup(false);

        const { found } = payload.data || {};
        onSelectedLocationsChange([...excludedLocations, ...found]);
      } catch (e) {
        toast.error('There was an error in handling pasted zip codes.');
        console.error((e as Error)?.message);
        setLoadingLookup(false);
      }
    },
    [excludedLocations, onSelectedLocationsChange],
  );

  const onChange = useCallback(
    (_: void, data: ILocation[]) => {
      onSelectedLocationsChange(data);
    },
    [onSelectedLocationsChange],
  );

  const onRemove = useCallback(
    (locationId: string) => () => {
      onSelectedLocationsChange(excludedLocations.filter((item: { id: string }) => locationId !== item.id));
    },
    [onSelectedLocationsChange, excludedLocations],
  );
  //#endregion

  //#region Targeting
  const getOptions = useCallback(
    (search: string) => {
      dispatch(getGeoOptions(search));
    },
    [dispatch],
  );

  const debouncedFetch = useMemo(() => {
    return debounce(getOptions, 350);
  }, [getOptions]);

  const onOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const onClose = useCallback(() => {
    setOpen(false);
    setSuggestions([]);
    setQuery('');
  }, []);

  useEffect(() => {
    if (!options?.length || !query) {
      setSuggestions([]);
      return;
    }
    setSuggestions(options);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  useEffect(() => {
    if (!query) {
      setSuggestions([]);
      return;
    }

    if (query.length < 2) return;

    debouncedFetch(query);

    return () => {
      debouncedFetch.clear();
    };
  }, [debouncedFetch, query]);

  useEffect(() => {
    if (currentChannel === Channel.Dooh) {
      onSelectedLocationsChange(
        [...(excludedLocations || [])].filter((location: { type: string }) => location.type === MAP_SCALES.ZIP),
      );
    }
  }, [currentChannel, onSelectedLocationsChange]);

  //#endregion

  const [isOpen, setIsOpen] = useState(false);
  const toggleCollapse = () => {
    setIsOpen(!isOpen);
  };

  const handleKeyDown = useCallback(
    async (event: KeyboardEvent) => {
      if (event.key === ' ' || event.key === ',') {
        event.preventDefault();
        const zip = query.trim();

        const symbol = event.key;

        if (ZIP_CODES_REGEX.test(zip)) {
          try {
            setLoadingLookup(true);
            const payload = await axios.post(GEO_TARGETING_LOCATIONS_LOOKUP, {
              zips: [zip],
            });
            setLoadingLookup(false);
            const { found, notFound } = payload.data || {};
            onSelectedLocationsChange([...(excludedLocations || []), ...(found || [])]);
            if (notFound?.length) {
              toast.info('Selected for exclusion zip code was not found');
            }
            setQuery('');
          } catch (error) {
            toast.error('There was an error, in checking zip code');
            console.error(error?.message);
            setLoadingLookup(false);
          }
        } else {
          setQuery(prev => prev + symbol);
        }
      }
    },
    [excludedLocations, onSelectedLocationsChange, query],
  );

  return (
    <div>
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'lightgray',
          borderRadius: '8px',
          overflow: 'hidden',
        }}
      >
        <Button
          onClick={toggleCollapse}
          endIcon={
            <ArrowDropDownIcon
              sx={[
                {
                  transition: '0.3s',
                },
                isOpen
                  ? {
                      transform: 'rotate(180deg)',
                    }
                  : {
                      transform: 'rotate(0)',
                    },
              ]}
            />
          }
          fullWidth
          sx={{
            justifyContent: 'space-between',
            backgroundColor: '#f5f5f5',
          }}
        >
          <div className='flex items-center gap-2'>
            <RemoveCircleOutlineIcon />
            Geo Exclusion
          </div>
        </Button>
        <Collapse in={isOpen}>
          <div className='flex flex-col gap-2 m-2'>
            <GeoTargeting
              selectedLocations={excludedLocations}
              loadingLookup={loadingLookup}
              loading={loading}
              query={query}
              handleInput={handleInput}
              handleKeyDown={handleKeyDown}
              onChange={onChange}
              onValuesPaste={onValuesPaste}
              onOpen={onOpen}
              onClose={onClose}
              suggestions={suggestions}
              open={open}
              id='geo-exclusion-autocomplete'
              placeholder={getGeoSearchPlaceholder(currentChannel)}
            />
            {excludedLocations?.length ? (
              <LocationChips
                locations={excludedLocations}
                variant='success'
                onRemove={onRemove}
                geoFenceLocation={geoFence?.address}
              />
            ) : null}
          </div>
        </Collapse>
      </Box>
    </div>
  );
};

export default GeoExclusion;
